<script lang="ts" setup>
import { ref, useTemplateRef } from 'vue';
import { IconSearch, IconClose } from '@/icons';
import { UiButtonIcon } from '../ui-button-icon';

interface Props {
  modelValue?: string;
  placeholder?: string;
}

const props = defineProps<Props>();

defineEmits<{
  'update:productId': [id: string];
  'update:modelValue': [value: string];
}>();

const modelValue = defineModel<string>('modelValue', { default: '' });
const inputRef = useTemplateRef('input');

const active = ref(false);

function focus() {
  inputRef.value?.focus();
}

function clear() {
  focus();
  modelValue.value = '';
}
</script>

<template>
  <div :class="$style.root">
    <UiButtonIcon variant="tertiary" @mousedown.prevent="focus()">
      <IconSearch />
    </UiButtonIcon>
    <div :class="[$style.container, { [$style.containerExpanded]: active || modelValue }]">
      <input
        ref="input"
        v-model="modelValue"
        type="text"
        :class="$style.input"
        :placeholder="props.placeholder"
        :tabindexx="active ? 0 : -1"
        @blur="active = false"
        @focus="active = true"
      />
      <div :class="$style.clearButton">
        <UiButtonIcon
          v-if="modelValue"
          variant="tertiary"
          size="s"
          @mousedown.prevent.capture="clear()"
        >
          <IconClose />
        </UiButtonIcon>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/typography';

.root {
  display: inline-flex;
  align-items: center;
}

.container {
  position: relative;
  overflow: hidden;
  width: 0;
  transition: width 0.3s ease;
}

.containerExpanded {
  width: 256px;
}

.clearButton {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.input {
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-transform: inherit;
  border-radius: inherit;
  background: none;
  border: 0;
  margin: 0;
  max-width: 100%;
  width: 100%;
  font-size: 13px;
  line-height: 16px;
  padding: 4px 24px 4px 4px;
  font-family: typography.$font-family-primary;
  font-weight: 400;
  color: #010000;
  caret-color: currentColor;
  outline: none;
  appearance: none;
  word-break: keep-all;
}
</style>
